import { cn } from "@/lib/utils/cn";

// Bumpować przy każdej zmianie pliku — Firebase Hosting trzyma PNG na 1 rok
// (immutable), więc PWA nie pobierze nowej grafiki bez nowego URL.
const PARENT_MASCOT_CACHE_VERSION = "5";

type Gender = "m" | "f";
export type ParentPose =
  | "default"
  | "info"
  | "finger-down"
  | "avatar"
  | "coffee"
  | "relaxed"
  | "check"
  | "overwhelmed"
  | "calm"
  | "heart"
  | "proud";
type Pose = ParentPose;

/**
 * Pliki per gender × pose × variant. Variant 0 = domyślna, kolejne = dodatkowe
 * grafiki dla tej samej pozy (rotacja przez `variant` prop).
 *
 * Dla gender × pose którego nie mamy w bibliotece (np. dad nie ma `heart`,
 * mom nie ma `check`) — fallbackujemy na inną sensowną pozę żeby nie
 * pokazywać 404. Patrz uwagi inline.
 */
const PARENT_FILES: Record<Gender, Record<Pose, readonly string[]>> = {
  m: {
    default: ["/parents-mascot/dad.png"],
    info: ["/parents-mascot/dad_info.png"],
    "finger-down": ["/parents-mascot/dad_finger_down.png"],
    avatar: ["/parents-mascot/dad_avatar.png"],
    coffee: ["/parents-mascot/dad_coffee.png"],
    relaxed: ["/parents-mascot/dad_relaxed.png"],
    check: ["/parents-mascot/dad_check.png"],
    overwhelmed: [
      "/parents-mascot/dad_overwhelmed_funny.png",
      "/parents-mascot/dad_overwhelmed_funny-2.png",
    ],
    calm: ["/parents-mascot/dad_calm.png"],
    // Brak dad_heart / dad_proud — fallback na default (brak `dad_thumbs_up`).
    heart: ["/parents-mascot/dad.png"],
    proud: ["/parents-mascot/dad.png"],
  },
  f: {
    default: ["/parents-mascot/mom.png"],
    info: ["/parents-mascot/mom_info.png"],
    "finger-down": ["/parents-mascot/mom_finger_down.png"],
    avatar: ["/parents-mascot/mom_avatar.png"],
    coffee: ["/parents-mascot/mom_coffee.png"],
    relaxed: ["/parents-mascot/mom_relaxed.png"],
    // Brak mom_check — fallback na default (mama bez clipboardu).
    check: ["/parents-mascot/mom.png"],
    // Mama dostała `proud` (dłonie splecione, dumna) zamiast „overwhelmed".
    overwhelmed: ["/parents-mascot/mom_proud.png"],
    calm: [
      "/parents-mascot/mom_calm.png",
      "/parents-mascot/mom_calm-2.png",
    ],
    heart: ["/parents-mascot/mom_heart.png"],
    proud: ["/parents-mascot/mom_proud.png"],
  },
};

type Props = {
  /** Płeć dorosłego — wybiera plik. Gdy null/undefined fallback na "m". */
  gender: Gender | null | undefined;
  /** Poza maskotki — `info` pokazuje rodzica w geście tłumaczenia. */
  pose?: Pose;
  /** Wariant w obrębie pozy (0 = pierwsza, modulo długość listy). */
  variant?: number;
  size?: number;
  flip?: boolean;
  shadow?: boolean;
  /**
   * Tryb avatara: img wypełnia rodzica (h-full w-full) z object-cover
   * object-top — wycina głowę z górnej części grafiki. Ignoruje `size`.
   */
  fill?: boolean;
  className?: string;
  alt?: string;
};

/**
 * Maskotka dorosłego (Tata / Mama). Towarzyszy rodzicowi w manifeście marki
 * i na ParentDashboard. Gdy gender nieznany — pokazujemy tatę jako default
 * (neutralny fallback, można zmienić w przyszłości na rotację).
 */
export function ParentMascot({
  gender,
  pose = "default",
  variant = 0,
  size = 120,
  flip = false,
  shadow = true,
  fill = false,
  className,
  alt,
}: Props) {
  const g: Gender = gender === "f" ? "f" : "m";
  // Fill mode = avatar use case → wariant przycięty do głowy+klatki,
  // natywnie 384×384 (mniejszy downscale w avatarach 80–96px).
  const effectivePose: Pose = fill ? "avatar" : pose;
  const files = PARENT_FILES[g][effectivePose];
  const file = files[variant % files.length];
  const src = `${file}?v=${PARENT_MASCOT_CACHE_VERSION}`;
  return (
    <img
      src={src}
      alt={alt ?? (g === "f" ? "Mama" : "Tata")}
      className={cn(
        fill
          ? "h-full w-full object-cover object-top block"
          : "block max-w-none",
        className,
      )}
      style={
        fill
          ? {
              imageRendering: "auto",
              transform: flip ? "scaleX(-1)" : undefined,
              filter: shadow
                ? "drop-shadow(0 6px 10px rgba(30,41,59,0.18))"
                : undefined,
            }
          : {
              height: size,
              width: "auto",
              transform: flip ? "scaleX(-1)" : undefined,
              filter: shadow
                ? "drop-shadow(0 6px 10px rgba(30,41,59,0.18))"
                : undefined,
            }
      }
    />
  );
}
